import each from "lodash/each";
import get from "lodash/get";
import numeral from "numeral";
import React from "react";
import utils, { aggregateBrokers, formatDateToDisplay } from "../utils/operations";
import constants from "./constants";

const DEAL_STAGE_VOUCHER = 2;

const viewHeaders = {
    broker_open: [
        {
            name: "Deal",
            prop: "transactionType",
            sort: "listing.listingKey",
        },
        {
            name: "Prop Type",
            prop: "propertyType", //todo
            type: "plaintext",
        },
        {
            name: "Deal Type",
            prop: "transactionType",
            type: "plaintext",
        },
        {
            name: "Square Feet",
            prop: "squareFeet",
            type: "plaintext",
            transform: (value) => {
                return numeral(value).format("0,0") + " SF";
            },
        },
        {
            name: "Installments",
            prop: "installments",
            type: "plaintext",
            transform: (value) => {
                return `1 of ${value.length + 1}`;
            },
            sort: ["currentInstallment.installmentNumber", "totalInstallments"],
        },
        {
            name: "Next Payment Date",
            prop: "currentInstallment.dueDate",
            type: "plaintext",
            transform: utils.formatDate("MM-DD-YYYY"),
        },
        {
            name: "Total KM Commission",
            prop: "totalKmCommission",
            type: "plaintext",
            transform: (value) => {
                return value > 0 ? "$ " + numeral(value).format("0,0.00") : "-";
            },
        },
        {
            name: "Step",
            prop: "dealStage",
            type: "plaintext",
            transform: (value) => {
                return value != null && value.stage === DEAL_STAGE_VOUCHER ? "Voucher" : "Comp";
            },
        },
        {
            name: "Status",
            prop: "dealStatus",
            type: "plaintext",
        },
    ],
    broker_closed: [
        {
            name: "Deal",
            prop: "propertyAttributes.address.address1",
            type: "hyperlink",
            sort: "dealKey",
            width: "18%",
        },
        {
            name: "Tenant/Buyer",
            prop: "buyerTenantName", //todo
            type: "plaintext",
            width: "16%",
        },
        {
            name: "Landlord/Seller",
            prop: "landlordSellerName",
            type: "plaintext",
            width: "16%",
        },
        {
            name: "Square Feet",
            prop: "squareFeet",
            type: "plaintext",
            transform: (value) => {
                return numeral(value).format("0,0") + " SF";
            },
        },
        {
            name: "Installments",
            prop: "installments",
            type: "plaintext",
            transform: (value) => {
                return `1 of ${value.length + 1}`;
            },
            sort: ["currentInstallment.installmentNumber", "totalInstallments"],
        },
        {
            name: "Batch Date",
            prop: "batchDate",
            type: "plaintext",
            transform: utils.formatDate("MM-DD-YYYY"),
            sortFunction: utils.formatDate("YYYY-MM-DD"),
        },
        {
            name: "My Amount",
            prop: `brokerNetAmount`,
            type: "plaintext",
            transform: (value) => {
                return value > 0 ? "$ " + numeral(value).format("0,0.00") : "-";
            },
        },
    ],
    data_entry_unassigned: [
        {
            name: "Deal",
            prop: "propertyAttributes.address.address1",
            type: "hyperlink",
            sort: "dealKey",
        },
        {
            name: "Brokers",
            prop: "brokerAgents",
            type: "aggregate",
            transform: (brokerAgents) => {
                return aggregateBrokers(brokerAgents, "person");
            },
        },
        {
            name: "Modified",
            prop: "auditLastModified",
            type: "plaintext",
            transform: utils.formatDate("MM-DD-YYYY"),
        },
        {
            name: "Prop Type",
            prop: "propertyType",
            type: "plaintext",
        },
        {
            name: "Deal Type",
            prop: "transactionType",
            type: "plaintext",
        },
        {
            name: "Total Commission",
            prop: "totalCommission",
            type: "plaintext",
            numeric: true,
            transform: (value) => {
                return "$ " + numeral(value).format("0,0.00");
            },
        },
        {
            name: "Status",
            prop: "dealStatus",
            type: "plaintext",
        },
    ],
    data_entry_open: [
        {
            name: "Deal",
            prop: "propertyAttributes.address.address1",
            type: "hyperlink",
            sort: "dealKey",
        },
        {
            name: "Brokers",
            prop: "brokerAgents",
            type: "aggregate",
            transform: (brokerAgents) => {
                return aggregateBrokers(brokerAgents, "person");
            },
        },
        {
            name: "Modified",
            prop: "auditLastModified",
            type: "plaintext",
            transform: utils.formatDate("MM-DD-YYYY"),
        },
        {
            name: "Prop Type",
            prop: "propertyType",
            type: "plaintext",
        },
        {
            name: "Deal Type",
            prop: "transactionType",
            type: "plaintext",
        },
        {
            name: "Installments",
            prop: "installments",
            type: "plaintext",
            transform: (value) => {
                return `1 of ${value.length + 1}`;
            },
            sort: ["currentInstallment.installmentNumber", "totalInstallments"],
        },
        {
            name: "Next Payment Date",
            prop: "currentInstallment.dueDate",
            type: "plaintext",
            transform: utils.formatDate("MM-DD-YYYY"),
        },
        {
            name: "Total Commission",
            prop: "totalCommission",
            type: "plaintext",
            numeric: true,
            transform: (value) => {
                return "$ " + numeral(value).format("0,0.00");
            },
        },
        {
            name: "Status",
            prop: "dealStatus",
            type: "plaintext",
        },
    ],
    data_admin_needs_final_review: [
        {
            name: "Deal",
            prop: "propertyAttributes.address.address1",
            type: "hyperlink",
            sort: "dealKey",
        },
        {
            name: "Brokers",
            prop: "brokerAgents",
            type: "aggregate",
            transform: (brokerAgents) => {
                return aggregateBrokers(brokerAgents, "person");
            },
        },
        {
            name: "Payment Attached Date",
            prop: "paymentAttachedDate",
            type: "plaintext",
            transform: utils.formatDate("MM-DD-YYYY"),
        },
        {
            name: "Prop Type",
            prop: "propertyType", //todo
            type: "plaintext",
        },
        {
            name: "Deal Type",
            prop: "transactionType",
            type: "plaintext",
        },
        {
            name: "Total Commission",
            prop: "totalCommission",
            type: "plaintext",
            numeric: true,
            transform: (value) => {
                return "$ " + numeral(value).format("0,0.00");
            },
        },
        {
            name: "Total KM Commission",
            prop: "totalKmCommission",
            type: "plaintext",
            numeric: true,
            transform: (value) => {
                return "$ " + numeral(value).format("0,0.00");
            },
        },
        {
            name: "Status",
            prop: "dealStatus",
            type: "plaintext",
        },
    ],
    portland_broker_admin: [
        {
            name: "Deal",
            prop: "propertyAttributes.address.address1",
            type: "hyperlink",
            sort: "listing.listingKey",
        },
        {
            name: "Brokers",
            prop: "brokerAgents",
            type: "aggregate",
            transform: (brokerAgents) => {
                return aggregateBrokers(brokerAgents, "person");
            },
        },
        {
            name: "Modified",
            prop: "auditLastModified",
            type: "plaintext",
            transform: utils.formatDate("MM-DD-YYYY"),
        },
        {
            name: "Reviewed By",
            prop: "reviewedBy",
            type: "plaintext",
        },
        {
            name: "Prop Type",
            prop: "propertyType",
            type: "plaintext",
        },
        {
            name: "Deal Type",
            prop: "transactionType",
            type: "plaintext",
        },
        {
            name: "Total KM Commission",
            prop: "totalKmCommission",
            type: "plaintext",
            numeric: true,
            transform: (value) => {
                return "$ " + numeral(value).format("0,0.00");
            },
        },
        {
            name: "Status",
            prop: "dealStatus",
            type: "plaintext",
        },
    ],
    portland_broker_admin_sale: [
        {
            name: "Deal",
            prop: "propertyAttributes.address.address1",
            type: "hyperlink",
            sort: "listing.listingKey",
        },
        {
            name: "Brokers",
            prop: "brokerAgents",
            type: "aggregate",
            transform: (brokerAgents) => {
                return aggregateBrokers(brokerAgents, "person");
            },
        },

        {
            name: "Created",
            prop: "auditCreated",
            type: "plaintext",
            transform: utils.formatDate("MM-DD-YYYY"),
        },
        {
            name: "Reviewed By",
            prop: "reviewedBy",
            type: "plaintext",
        },
        {
            name: "Prop Type",
            prop: "propertyType", //todo
            type: "plaintext",
        },
        {
            name: "Deal Type",
            prop: "transactionType",
            type: "plaintext",
        },
        {
            name: "Documents Needing Approval",
            prop: "documentsRequiringApproval",
            type: "plaintext",
        },
        {
            name: "Status",
            prop: "dealStatus",
            type: "plaintext",
        },
    ],
    portland_broker_admin_closed: [
        {
            name: "Deal",
            prop: "propertyAttributes.address.address1",
            type: "hyperlink",
            sort: "listing.listingKey",
        },
        {
            name: "Brokers",
            prop: "brokerAgents",
            type: "aggregate",
            transform: (brokerAgents) => {
                return aggregateBrokers(brokerAgents, "person");
            },
        },
        {
            name: "Modified",
            prop: "auditLastModified",
            type: "plaintext",
            transform: utils.formatDate("MM-DD-YYYY"),
        },
        {
            name: "Reviewed By",
            prop: "reviewedBy",
            type: "plaintext",
        },
        {
            name: "Prop Type",
            prop: "propertyType",
            type: "plaintext",
        },
        {
            name: "Deal Type",
            prop: "transactionType",
            type: "plaintext",
        },
        {
            name: "Total KM Commission",
            prop: "totalKmCommission",
            type: "plaintext",
            numeric: true,
            transform: (value) => {
                return "$ " + numeral(value).format("0,0.00");
            },
        },
        {
            name: "Status",
            prop: "dealStatus",
            type: "plaintext",
        },
    ],
    arizona_broker_admin_review: [
        {
            name: "Deal",
            prop: "propertyAttributes.address.address1",
            type: "hyperlink",
            sort: "listing.listingKey",
        },
        {
            name: "Brokers",
            prop: "brokerAgents",
            type: "aggregate",
            transform: (brokerAgents) => {
                return aggregateBrokers(brokerAgents, "person");
            },
        },
        {
            name: "Modified",
            prop: "auditLastModified",
            type: "plaintext",
            transform: utils.formatDate("MM-DD-YYYY"),
        },
        {
            name: "Prop Type",
            prop: "propertyType",
            type: "plaintext",
        },
        {
            name: "Deal Type",
            prop: "transactionType",
            type: "plaintext",
        },
        {
            name: "Total KM Commission",
            prop: "totalKmCommission",
            type: "plaintext",
            numeric: true,
            transform: (value) => {
                return "$ " + numeral(value).format("0,0.00");
            },
        },
        {
            name: "Status",
            prop: "dealStatus",
            type: "plaintext",
        },
    ],
    arizona_broker_admin_closed: [
        {
            name: "Deal",
            prop: "propertyAttributes.address.address1",
            type: "hyperlink",
            sort: "listing.listingKey",
        },
        {
            name: "Brokers",
            prop: "brokerAgents",
            type: "aggregate",
            transform: (brokerAgents) => {
                return aggregateBrokers(brokerAgents, "person");
            },
        },
        {
            name: "Modified",
            prop: "auditLastModified",
            type: "plaintext",
            transform: utils.formatDate("MM-DD-YYYY"),
        },
        {
            name: "Prop Type",
            prop: "propertyType",
            type: "plaintext",
        },
        {
            name: "Deal Type",
            prop: "transactionType",
            type: "plaintext",
        },
        {
            name: "Total KM Commission",
            prop: "totalKmCommission",
            type: "plaintext",
            numeric: true,
            transform: (value) => {
                return "$ " + numeral(value).format("0,0.00");
            },
        },
        {
            name: "Status",
            prop: "dealStatus",
            type: "plaintext",
        },
    ],
};

const columnsToQuery = [
    "deal.propertyAttributes.address.address1",
    "paymentTotal",
    "auditLastModified",
    "voucherKey",
    "voucherStatus",
    "dealKey",
];

const brokerColumnsToQuery = [
    "propertyType",
    "transactionType",
    "dealStatus",
    "landlordSellerName",
    "buyerTenantName",
    "address1",
    "city",
    "state",
    "zip",
    "listingName",
    "dealKey",
    "brokerNames",
    "listing.property.primaryAddress.address1",
    "listing.listingName",
    "listingName",
    "totalKmCommission",
    "installments",
    "currentInstallment",
];

const dataEntryColumnsToQuery = [
    "propertyAttributes.address.address1",
    "propertyType",
    "transactionType",
    "propertyType",
    "dealStatus",
    "listing.listingKey",
    "listing.property.primaryAddress.address1",
    "listing.property.primaryAddress.city.city",
    "auditLastModified",
    "currentSf",
    "brokerNames",
    "dealKey",
    "installments",
    "currentInstallment",
];

const dataAdminColumnsToQuery = [
    "propertyAttributes.address.address1",
    "propertyType",
    "transactionType",
    "propertyType",
    "dealStatus",
    "listing.listingKey",
    "auditLastModified",
    "currentSf",
    "brokerNames",
    "listing.property.primaryAddress.address1",
    "listing.property.primaryAddress.city.city",
    "dealKey",
];

const tabMetaData = {
    portland_broker_admin_sale: {
        refreshOnSwitch: true,
    },
    portland_broker_admin: {
        refreshOnSwitch: true,
    },
};

const filterByQuery = (data, columnsToQuery, query) => {
    let included = false;
    each(columnsToQuery, (column) => {
        if (get(data, column)) {
            if (get(data, column.toString()).toString().toLowerCase().includes(query)) {
                included = true;
            }
        }
    });
    return included;
};

const refreshTabOnSwitch = (tab) => {
    let refresh = false;
    if (tab && tabMetaData[tab]) {
        refresh = tabMetaData[tab].refreshOnSwitch;
    }
    return refresh;
};

const createColumnsConfig = (components, functions, props) => {
    const {
        StyledDealCell,
        StyledLink,
        StyledDealName,
        StyledReviewLink,
        StyledUndoSpan,
        StatusChip,
        StyledReviewedByDiv,
    } = components;
    const { buildAddress, formatDate, renderBrokers, updateReviewStatus, getDealStatus, isTerminated } = functions;
    const { dealView } = props;

    return {
        broker_open: [
            {
                field: "dealName",
                headerName: "Deal",
                flex: 2,
                minWidth: 250,
                renderCell: (params) => (
                    <StyledDealCell>
                        <StyledLink
                            to={
                                params.row?.transactionType === "Consulting"
                                    ? `/voucher/${params.row.voucherId}`
                                    : `/deal/${params.id}`
                            }
                            target="_blank"
                        >
                            <StyledDealName>
                                {`Deal No. ${params.id}${params.row?.dealName ? ` | ${params.row.dealName}` : ""}`}
                            </StyledDealName>
                        </StyledLink>
                    </StyledDealCell>
                ),
            },
            {
                field: "address",
                headerName: "Address",
                flex: 2,
                minWidth: 270,
                sortable: false,
                renderCell: (params) => (
                    <StyledDealCell>{buildAddress(params.row?.primaryAddress ?? {})}</StyledDealCell>
                ),
            },
            {
                field: "propertyType",
                headerName: "Type",
                flex: 1,
                minWidth: 120,
                sortable: false,
                renderCell: (params) => `${params.row?.propertyType ?? ""} ${params.row?.transactionType ?? ""}`,
            },
            {
                field: "squareFeet",
                headerName: "Square Feet",
                flex: 1,
                minWidth: 110,
                renderCell: (params) => `${params.row?.squareFeet} SF`,
            },
            {
                field: "currentInstallment",
                headerName: "Installments",
                flex: 1,
                minWidth: 95,
                renderCell: (params) => (
                    <div style={{ marginLeft: "1vw" }}>
                        {`${params.row?.currentInstallment} of ${params.row?.totalInstallments}`}
                    </div>
                ),
            },
            {
                field: "nextPaymentDate",
                headerName: "Next Payment Date",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => {
                    return params.row?.nextPaymentDate ? formatDate("MM-DD-YYYY")(params.row.nextPaymentDate) : "N/A";
                },
            },
            {
                field: "totalKmCommission",
                headerName: "Total KM Commission",
                hide: true,
                flex: 1,
                minWidth: 125,
                renderCell: (params) =>
                    params.row?.totalKmCommission ? "$ " + numeral(params.row.totalKmCommission).format("0,0.00") : "-",
            },
            {
                field: "step",
                headerName: "Step",
                hide: true,
                flex: 1,
                minWidth: 65,
                renderCell: (params) => params.row?.step,
            },
            {
                field: "dealStatus",
                headerName: "Status",
                flex: 1,
                minWidth: 170,
                renderCell: (params) => {
                    return <StatusChip status={getDealStatus(params.row?.status)} />;
                },
            },
        ],
        broker_closed: [
            {
                field: "dealName",
                headerName: "Deal",
                flex: 2,
                minWidth: 270,
                renderCell: (params) => (
                    <StyledDealCell>
                        <StyledLink
                            to={
                                params.row?.transactionType === "Consulting"
                                    ? `/voucher/${params.row.voucherId}`
                                    : `/deal/${params.id}`
                            }
                            target="_blank"
                        >
                            <StyledDealName isTerminated={isTerminated(params.row?.status)}>
                                {`Deal No. ${params.id}${params.row?.dealName ? ` | ${params.row.dealName}` : ""}`}
                            </StyledDealName>
                        </StyledLink>
                    </StyledDealCell>
                ),
            },
            {
                field: "address",
                headerName: "Address",
                flex: 2,
                minWidth: 255,
                sortable: false,
                renderCell: (params) => (
                    <StyledDealCell>{buildAddress(params.row?.primaryAddress ?? {})}</StyledDealCell>
                ),
            },
            {
                field: "buyerTenantName",
                headerName: "Tenant/Buyer",
                flex: 1,
                minWidth: 190,
                renderCell: (params) => params.row?.buyerTenantName,
            },
            {
                field: "landlordSellerName",
                headerName: "Landlord/Seller",
                flex: 1,
                minWidth: 190,
                renderCell: (params) => params.row?.landlordSellerName,
            },
            {
                field: "squareFeet",
                headerName: "Square Feet",
                flex: 1,
                minWidth: 110,
                renderCell: (params) => `${params.row?.squareFeet} SF`,
            },
            {
                field: "totalInstallments",
                headerName: "Total installments",
                flex: 1,
                minWidth: 95,
                renderCell: (params) => <div style={{ marginLeft: "1vw" }}>{params.row?.totalInstallments}</div>,
            },
            {
                field: "batchDate",
                headerName: "Batch Date",
                flex: 1,
                minWidth: 100,
                renderCell: (params) => {
                    return params.row?.batchDate ? formatDate("MM-DD-YYYY")(params.row.batchDate) : "N/A";
                },
            },
            {
                field: "brokerNetAmount",
                headerName: "My Amount",
                flex: 1,
                minWidth: 120,
                renderCell: (params) =>
                    params.row?.brokerNetAmount ? "$ " + numeral(params.row.brokerNetAmount).format("0,0.00") : "-",
            },
        ],
        portland_broker_admin_sale: [
            {
                field: "dealName",
                headerName: "Deal",
                flex: 2,
                minWidth: 270,
                renderCell: (params) => (
                    <StyledDealCell>
                        <StyledLink
                            to={
                                params.row?.transactionType === "Consulting"
                                    ? `/voucher/${params.row.voucherId}`
                                    : `/deal/${params.id}`
                            }
                            target="_blank"
                        >
                            <StyledDealName>
                                {`Deal No. ${params.id}${params.row?.dealName ? ` | ${params.row.dealName}` : ""}`}
                            </StyledDealName>
                        </StyledLink>
                    </StyledDealCell>
                ),
            },
            {
                field: "address",
                headerName: "Address",
                flex: 2,
                minWidth: 200,
                sortable: false,
                renderCell: (params) => (
                    <StyledDealCell>{buildAddress(params.row?.primaryAddress ?? {})}</StyledDealCell>
                ),
            },
            {
                field: "brokers",
                headerName: "Brokers",
                flex: 1,
                minWidth: 150,
                renderCell: (params) => renderBrokers(params.row?.brokers),
            },
            {
                field: "auditCreatedDate",
                headerName: "Created",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => {
                    return params.row?.auditCreatedDate ? formatDate("MM-DD-YYYY")(params.row.auditCreatedDate) : "N/A";
                },
            },
            {
                field: "reviewedBy",
                headerName: "Reviewed By",
                flex: 1,
                minWidth: 255,
                renderCell: (params) => {
                    return params.row?.reviewedBy ? (
                        <React.Fragment>
                            <div>
                                <StyledReviewedByDiv>{params.row.reviewedBy}</StyledReviewedByDiv>
                                {formatDateToDisplay(params.row.reviewedDate)}
                                <StyledUndoSpan onClick={() => updateReviewStatus(true, params.id, dealView)}>
                                    (undo)
                                </StyledUndoSpan>
                            </div>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <StyledReviewLink onClick={() => updateReviewStatus(false, params.id, dealView)}>
                                Review
                            </StyledReviewLink>
                        </React.Fragment>
                    );
                },
            },
            {
                field: "propertyType",
                headerName: "Type",
                flex: 1,
                minWidth: 120,
                sortable: false,
                renderCell: (params) => `${params.row?.propertyType ?? ""} ${params.row?.transactionType ?? ""}`,
            },
            {
                field: "documentsRequiringApproval",
                headerName: "Documents Needing Approval",
                flex: 1,
                minWidth: 100,
                renderHeader: () => (
                    <div
                        style={{
                            color: "rgb(158, 158, 158)",
                            fontSize: "12px",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            textAlign: "left",
                        }}
                    >
                        Documents
                        <br />
                        Needing
                        <br />
                        Approval
                    </div>
                ),
                renderCell: (params) => params.row?.documentsRequiringApproval,
            },
            {
                field: "dealStatus",
                headerName: "Status",
                flex: 1,
                minWidth: 170,
                renderCell: (params) => {
                    return <StatusChip status={getDealStatus(params.row?.dealStatus)} />;
                },
            },
        ],
        portland_broker_admin: [
            {
                field: "dealName",
                headerName: "Deal",
                flex: 2,
                minWidth: 270,
                renderCell: (params) => (
                    <StyledDealCell>
                        <StyledLink
                            to={
                                params.row?.transactionType === "Consulting"
                                    ? `/voucher/${params.row.voucherId}`
                                    : `/deal/${params.id}`
                            }
                            target="_blank"
                        >
                            <StyledDealName>
                                {`Deal No. ${params.id}${params.row?.dealName ? ` | ${params.row.dealName}` : ""}`}
                            </StyledDealName>
                        </StyledLink>
                    </StyledDealCell>
                ),
            },
            {
                field: "address",
                headerName: "Address",
                flex: 2,
                minWidth: 200,
                sortable: false,
                renderCell: (params) => (
                    <StyledDealCell>{buildAddress(params.row?.primaryAddress ?? {})}</StyledDealCell>
                ),
            },
            {
                field: "brokers",
                headerName: "Brokers",
                flex: 1,
                minWidth: 150,
                renderCell: (params) => renderBrokers(params.row?.brokers),
            },
            {
                field: "auditLastModified",
                headerName: "Modified",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => {
                    return params.row?.auditLastModified
                        ? formatDate("MM-DD-YYYY")(params.row.auditLastModified)
                        : "N/A";
                },
            },
            {
                field: "reviewedBy",
                headerName: "Reviewed By",
                flex: 1,
                minWidth: 255,
                renderCell: (params) => {
                    return params.row?.reviewedBy ? (
                        <React.Fragment>
                            <div>
                                <StyledReviewedByDiv>{params.row.reviewedBy}</StyledReviewedByDiv>
                                {formatDateToDisplay(params.row.reviewedDate)}
                                <StyledUndoSpan onClick={() => updateReviewStatus(true, params.id, dealView)}>
                                    (undo)
                                </StyledUndoSpan>
                            </div>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <StyledReviewLink onClick={() => updateReviewStatus(false, params.id, dealView)}>
                                Review
                            </StyledReviewLink>
                        </React.Fragment>
                    );
                },
            },
            {
                field: "propertyType",
                headerName: "Type",
                flex: 1,
                minWidth: 110,
                sortable: false,
                renderCell: (params) => `${params.row?.propertyType ?? ""} ${params.row?.transactionType ?? ""}`,
            },
            {
                field: "totalKmCommission",
                headerName: "Total KM Commission",
                flex: 1,
                minWidth: 120,
                renderCell: (params) =>
                    params.row?.totalKmCommission ? "$ " + numeral(params.row.totalKmCommission).format("0,0.00") : "-",
            },
            {
                field: "dealStatus",
                headerName: "Status",
                flex: 1,
                minWidth: 180,
                renderCell: (params) => {
                    return <StatusChip status={getDealStatus(params.row?.dealStatus)} />;
                },
            },
        ],
        portland_broker_admin_closed: [
            {
                field: "dealName",
                headerName: "Deal",
                flex: 2,
                minWidth: 270,
                renderCell: (params) => (
                    <StyledDealCell>
                        <StyledLink
                            to={
                                params.row?.transactionType === "Consulting"
                                    ? `/voucher/${params.row.voucherId}`
                                    : `/deal/${params.id}`
                            }
                            target="_blank"
                        >
                            <StyledDealName isTerminated={isTerminated(params.row?.dealStatus)}>
                                {`Deal No. ${params.id}${params.row?.dealName ? ` | ${params.row.dealName}` : ""}`}
                            </StyledDealName>
                        </StyledLink>
                    </StyledDealCell>
                ),
            },
            {
                field: "address",
                headerName: "Address",
                flex: 2,
                minWidth: 200,
                sortable: false,
                renderCell: (params) => (
                    <StyledDealCell>{buildAddress(params.row?.primaryAddress ?? {})}</StyledDealCell>
                ),
            },
            {
                field: "brokers",
                headerName: "Brokers",
                flex: 1,
                minWidth: 150,
                renderCell: (params) => renderBrokers(params.row?.brokers),
            },
            {
                field: "auditLastModified",
                headerName: "Modified",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => {
                    return params.row?.auditLastModified
                        ? formatDate("MM-DD-YYYY")(params.row.auditLastModified)
                        : "N/A";
                },
            },
            {
                field: "reviewedBy",
                headerName: "Reviewed By",
                flex: 1,
                minWidth: 255,
                renderCell: (params) => {
                    return params.row?.reviewedBy ? (
                        <React.Fragment>
                            <div>
                                <StyledReviewedByDiv>{params.row.reviewedBy}</StyledReviewedByDiv>
                                {formatDateToDisplay(params.row.reviewedDate)}
                                <StyledUndoSpan onClick={() => updateReviewStatus(true, params.id, dealView)}>
                                    (undo)
                                </StyledUndoSpan>
                            </div>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <StyledReviewLink onClick={() => updateReviewStatus(false, params.id, dealView)}>
                                Review
                            </StyledReviewLink>
                        </React.Fragment>
                    );
                },
            },
            {
                field: "propertyType",
                headerName: "Type",
                flex: 1,
                minWidth: 120,
                sortable: false,
                renderCell: (params) => `${params.row?.propertyType ?? ""} ${params.row?.transactionType ?? ""}`,
            },
            {
                field: "totalKmCommission",
                headerName: "Total KM Commission",
                flex: 1,
                minWidth: 150,
                renderCell: (params) =>
                    params.row?.totalKmCommission ? "$ " + numeral(params.row.totalKmCommission).format("0,0.00") : "-",
            },
            {
                field: "dealStatus",
                headerName: "Status",
                flex: 1,
                minWidth: 170,
                renderCell: (params) => {
                    return <StatusChip status={getDealStatus(params.row?.dealStatus)} />;
                },
            },
        ],
        arizona_broker_admin_review: [
            {
                field: "dealName",
                headerName: "Deal",
                flex: 2,
                minWidth: 270,
                renderCell: (params) => (
                    <StyledDealCell>
                        <StyledLink
                            to={
                                params.row?.transactionType === "Consulting"
                                    ? `/voucher/${params.row.voucherId}`
                                    : `/deal/${params.id}`
                            }
                            target="_blank"
                        >
                            <StyledDealName>
                                {`Deal No. ${params.id}${params.row?.dealName ? ` | ${params.row.dealName}` : ""}`}
                            </StyledDealName>
                        </StyledLink>
                    </StyledDealCell>
                ),
            },
            {
                field: "address",
                headerName: "Address",
                flex: 2,
                minWidth: 260,
                sortable: false,
                renderCell: (params) => (
                    <StyledDealCell>{buildAddress(params.row?.primaryAddress ?? {})}</StyledDealCell>
                ),
            },
            {
                field: "brokers",
                headerName: "Brokers",
                flex: 1,
                minWidth: 150,
                renderCell: (params) => renderBrokers(params.row?.brokers),
            },
            {
                field: "auditLastModified",
                headerName: "Modified",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => {
                    return params.row?.auditLastModified
                        ? formatDate("MM-DD-YYYY")(params.row.auditLastModified)
                        : "N/A";
                },
            },
            {
                field: "propertyType",
                headerName: "Type",
                flex: 1,
                minWidth: 120,
                sortable: false,
                renderCell: (params) => `${params.row?.propertyType ?? ""} ${params.row?.transactionType ?? ""}`,
            },
            {
                field: "totalKmCommission",
                headerName: "Total KM Commission",
                flex: 1,
                minWidth: 150,
                renderCell: (params) =>
                    params.row?.totalKmCommission ? "$ " + numeral(params.row.totalKmCommission).format("0,0.00") : "-",
            },
            {
                field: "dealStatus",
                headerName: "Status",
                flex: 1,
                minWidth: 170,
                renderCell: (params) => {
                    return <StatusChip status={getDealStatus(params.row?.dealStatus)} />;
                },
            },
        ],
        arizona_broker_admin_closed: [
            {
                field: "dealName",
                headerName: "Deal",
                flex: 2,
                minWidth: 270,
                renderCell: (params) => (
                    <StyledDealCell>
                        <StyledLink
                            to={
                                params.row?.transactionType === "Consulting"
                                    ? `/voucher/${params.row.voucherId}`
                                    : `/deal/${params.id}`
                            }
                            target="_blank"
                        >
                            <StyledDealName isTerminated={isTerminated(params.row?.dealStatus)}>
                                {`Deal No. ${params.id}${params.row?.dealName ? ` | ${params.row.dealName}` : ""}`}
                            </StyledDealName>
                        </StyledLink>
                    </StyledDealCell>
                ),
            },
            {
                field: "address",
                headerName: "Address",
                flex: 2,
                minWidth: 260,
                sortable: false,
                renderCell: (params) => (
                    <StyledDealCell>{buildAddress(params.row?.primaryAddress ?? {})}</StyledDealCell>
                ),
            },
            {
                field: "brokers",
                headerName: "Brokers",
                flex: 1,
                minWidth: 150,
                renderCell: (params) => renderBrokers(params.row?.brokers),
            },
            {
                field: "auditLastModified",
                headerName: "Modified",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => {
                    return params.row?.auditLastModified
                        ? formatDate("MM-DD-YYYY")(params.row.auditLastModified)
                        : "N/A";
                },
            },
            {
                field: "propertyType",
                headerName: "Type",
                flex: 1,
                minWidth: 120,
                sortable: false,
                renderCell: (params) => `${params.row?.propertyType ?? ""} ${params.row?.transactionType ?? ""}`,
            },
            {
                field: "totalKmCommission",
                headerName: "Total KM Commission",
                flex: 1,
                minWidth: 150,
                renderCell: (params) =>
                    params.row?.totalKmCommission ? "$ " + numeral(params.row.totalKmCommission).format("0,0.00") : "-",
            },
            {
                field: "dealStatus",
                headerName: "Status",
                flex: 1,
                minWidth: 170,
                renderCell: (params) => {
                    return <StatusChip status={getDealStatus(params.row?.dealStatus)} />;
                },
            },
        ],
    };
};

const brokerOpenDealsFilterColumns = [
    { field: "dealName", label: "Deal Name", type: "plaintext" },
    { field: "addressLine", label: "Address Line", type: "plaintext" },
    { field: "city", label: "City", type: "plaintext" },
    { field: "state", label: "State", type: "plaintext" },
    { field: "zipCode", label: "ZipCode", type: "plaintext" },
    { field: "squareFeet", label: "Square Feet", type: "numberRange" },
    { field: "currentInstallment", label: "Current Installment", type: "plaintext" },
    { field: "nextPaymentDate", label: "Next Payment Date", type: "date" },
    { field: "totalKmCommission", label: "Total KM Commission", type: "currency" },
    { field: "step", label: "Step", type: "plaintext" },
    { field: "dealStatus", label: "Status", type: "plaintext" },
];

const brokerClosedDealsFilterColumns = [
    { field: "dealName", label: "Deal Name", type: "plaintext" },
    { field: "addressLine", label: "Address Line", type: "plaintext" },
    { field: "city", label: "City", type: "plaintext" },
    { field: "state", label: "State", type: "plaintext" },
    { field: "zipCode", label: "ZipCode", type: "plaintext" },
    { field: "buyerTenantName", label: "Tenant/Buyer", type: "plaintext" },
    { field: "landlordSellerName", label: "Landlord/Seller", type: "plaintext" },
    { field: "squareFeet", label: "Square Feet", type: "numberRange" },
    { field: "totalInstallments", label: "Total installments", type: "plaintext" },
    { field: "batchDate", label: "Batch Date", type: "date" },
    { field: "brokerNetAmount", label: "My Amount", type: "currency" },
];

const portlandBrokerAdminSaleFilterColumns = [
    { field: "dealName", label: "Deal Name", type: "plaintext" },
    { field: "addressLine", label: "Address Line", type: "plaintext" },
    { field: "city", label: "City", type: "plaintext" },
    { field: "state", label: "State", type: "plaintext" },
    { field: "zipCode", label: "ZipCode", type: "plaintext" },
    { field: "brokers", label: "Broker", type: "plaintext" },
    { field: "auditCreatedDate", label: "Created", type: "date" },
    { field: "reviewedBy", label: "Reviewed By", type: "plaintext" },
    { field: "documentsRequiringApproval", label: "Documents Needing Approval", type: "plaintext" },
    { field: "dealStatus", label: "Status", type: "plaintext" },
];

const portlandBrokerAdminReviewFilterColumns = [
    { field: "dealName", label: "Deal Name", type: "plaintext" },
    { field: "addressLine", label: "Address Line", type: "plaintext" },
    { field: "city", label: "City", type: "plaintext" },
    { field: "state", label: "State", type: "plaintext" },
    { field: "zipCode", label: "ZipCode", type: "plaintext" },
    { field: "brokers", label: "Broker", type: "plaintext" },
    { field: "auditLastModified", label: "Modified", type: "date" },
    { field: "reviewedBy", label: "Reviewed By", type: "plaintext" },
    { field: "totalKmCommission", label: "Total KM Commission", type: "currency" },
    { field: "dealStatus", label: "Status", type: "plaintext" },
];

const portlandBrokerAdminClosedFilterColumns = [
    { field: "dealName", label: "Deal Name", type: "plaintext" },
    { field: "addressLine", label: "Address Line", type: "plaintext" },
    { field: "city", label: "City", type: "plaintext" },
    { field: "state", label: "State", type: "plaintext" },
    { field: "zipCode", label: "ZipCode", type: "plaintext" },
    { field: "brokers", label: "Broker", type: "plaintext" },
    { field: "auditLastModified", label: "Modified", type: "date" },
    { field: "reviewedBy", label: "Reviewed By", type: "plaintext" },
    { field: "totalKmCommission", label: "Total KM Commission", type: "currency" },
    { field: "dealStatus", label: "Status", type: "plaintext" },
];

const arizonaBrokerAdminReviewFilterColumns = [
    { field: "dealName", label: "Deal Name", type: "plaintext" },
    { field: "addressLine", label: "Address Line", type: "plaintext" },
    { field: "city", label: "City", type: "plaintext" },
    { field: "state", label: "State", type: "plaintext" },
    { field: "zipCode", label: "ZipCode", type: "plaintext" },
    { field: "brokers", label: "Broker", type: "plaintext" },
    { field: "auditLastModified", label: "Modified", type: "date" },
    { field: "totalKmCommission", label: "Total KM Commission", type: "currency" },
    { field: "dealStatus", label: "Status", type: "plaintext" },
];

const arizonaBrokerAdminClosedFilterColumns = [
    { field: "dealName", label: "Deal Name", type: "plaintext" },
    { field: "addressLine", label: "Address Line", type: "plaintext" },
    { field: "city", label: "City", type: "plaintext" },
    { field: "state", label: "State", type: "plaintext" },
    { field: "zipCode", label: "ZipCode", type: "plaintext" },
    { field: "brokers", label: "Broker", type: "plaintext" },
    { field: "auditLastModified", label: "Modified", type: "date" },
    { field: "totalKmCommission", label: "Total KM Commission", type: "currency" },
    { field: "dealStatus", label: "Status", type: "plaintext" },
];

const getDealsViewConfig = (user, components, functions, props) => {
    const columns = createColumnsConfig(components, functions, props);
    return {
        broker_open: {
            url: constants.urls.broker_open_deals(user),
            columns: columns.broker_open,
            filterColumns: brokerOpenDealsFilterColumns,
        },
        broker_closed: {
            url: constants.urls.broker_closed(user),
            columns: columns.broker_closed,
            filterColumns: brokerClosedDealsFilterColumns,
        },
        portland_broker_admin: {
            url: constants.urls.portland_broker_admin(),
            columns: columns.portland_broker_admin,
            filterColumns: portlandBrokerAdminReviewFilterColumns,
        },
        portland_broker_admin_sale: {
            url: constants.urls.portland_broker_admin_sale(),
            columns: columns.portland_broker_admin_sale,
            filterColumns: portlandBrokerAdminSaleFilterColumns,
        },
        portland_broker_admin_closed: {
            url: constants.urls.portland_broker_admin_closed(),
            columns: columns.portland_broker_admin_closed,
            filterColumns: portlandBrokerAdminClosedFilterColumns,
        },
        arizona_broker_admin_review: {
            url: constants.urls.arizona_broker_admin_review(),
            columns: columns.arizona_broker_admin_review,
            filterColumns: arizonaBrokerAdminReviewFilterColumns,
        },
        arizona_broker_admin_closed: {
            url: constants.urls.arizona_broker_admin_closed(),
            columns: columns.arizona_broker_admin_closed,
            filterColumns: arizonaBrokerAdminClosedFilterColumns,
        },
    };
};

export default {
    viewHeaders,
    columnsToQuery,
    brokerColumnsToQuery,
    dataEntryColumnsToQuery,
    dataAdminColumnsToQuery,
    filterByQuery,
    refreshTabOnSwitch,
    getDealsViewConfig,
    createColumnsConfig,
};
